import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const MESES_CURTOS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export type DashboardVolumeMensal = {
  mes: string;
  mesKey: string;
  clientes: number;
  projetos: number;
  pedidos: number;
  paineis: number;
};

type RpcRow = {
  mes: number;
  clientes: number | string;
  projetos: number | string;
  pedidos: number | string;
  paineis: number | string;
};

/**
 * Volume mensal do ano corrente via RPC agregada (poucas linhas).
 * Evita o limite de ~1000 rows do PostgREST e reduz egress vs. baixar todos os registros.
 * RLS continua valendo (SECURITY INVOKER).
 */
export function useDashboardVolumeMensal() {
  const { user, loading } = useAuth();
  const year = new Date().getFullYear();

  return useQuery({
    queryKey: ["dashboard", "volume-mensal-ano", year, user?.id ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_dashboard_volume_mensal", {
        p_ano: year,
      });

      if (error) throw error;

      const rows = (data || []) as RpcRow[];
      const series: DashboardVolumeMensal[] = rows.map((row) => {
        const mesNum = Number(row.mes);
        return {
          mes: MESES_CURTOS[mesNum - 1] || String(mesNum),
          mesKey: `${year}-${String(mesNum).padStart(2, "0")}`,
          clientes: Number(row.clientes) || 0,
          projetos: Number(row.projetos) || 0,
          pedidos: Number(row.pedidos) || 0,
          paineis: Number(row.paineis) || 0,
        };
      });

      return {
        year,
        series,
        totais: {
          clientes: series.reduce((s, x) => s + x.clientes, 0),
          projetos: series.reduce((s, x) => s + x.projetos, 0),
          pedidos: series.reduce((s, x) => s + x.pedidos, 0),
          paineis: series.reduce((s, x) => s + x.paineis, 0),
        },
      };
    },
    enabled: !loading,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
